/**
 * synthesizeBuffer — produce the initial CREATE TABLE buffer for the Table
 * Designer's hidden Monaco view.
 *
 * Two paths:
 *   • Create mode: blank `CREATE TABLE [name] ();` template.
 *   • Alter mode: fetch existing object metadata, then emit a fully-qualified
 *     CREATE script via the engine's script provider.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice 0.5 — Table Designer Migration.
 */
import type { UserFacingEngineId } from '@/core/engines';
import { getScriptProvider } from '@/features/scripting';
import type { TableMetadata } from '@/features/scripting';

export type SynthesizeCreateContext = {
  readonly engine: UserFacingEngineId;
  readonly schema: string;
  readonly tableName: string;
};

export type SynthesizeAlterContext = {
  readonly engine: UserFacingEngineId;
  readonly database: string;
  readonly schema: string;
  readonly tableName: string;
};

/**
 * Build the blank `CREATE TABLE` template used when the Designer opens for a
 * net-new table. The provider's `qualifyTableName` ensures dialect-correct
 * quoting (e.g. `[dbo].[Foo]` vs `"public"."foo"`).
 */
export function synthesizeBlankCreateTable(ctx: SynthesizeCreateContext): string {
  const provider = getScriptProvider(ctx.engine);
  const placeholderName = ctx.tableName.trim() === '' ? 'NewTable' : ctx.tableName;
  const qualified = provider.qualifyTableName(ctx.schema, placeholderName);
  return `CREATE TABLE ${qualified} (\n  -- Add columns here\n);\n`;
}

/**
 * Build the CREATE TABLE script from existing-table metadata. The script
 * provider's `generateTableScript('create', { metadata })` is the authoritative
 * source of truth for engine-specific syntax.
 */
export function synthesizeCreateFromMetadata(
  ctx: SynthesizeAlterContext,
  metadata: TableMetadata,
): string {
  const provider = getScriptProvider(ctx.engine);
  return provider.generateTableScript('create', {
    database: ctx.database,
    schema: ctx.schema,
    tableName: ctx.tableName,
    metadata,
  });
}

/**
 * Fetch table metadata via the IPC bridge and emit the CREATE script. Returns
 * `null` when the metadata API is unavailable or the fetch fails. Callers
 * surface error state — synthesize never throws.
 */
export async function synthesizeAlterTableBuffer(
  ctx: SynthesizeAlterContext,
  connectionId: string,
): Promise<{ readonly sql: string | null; readonly error: string | null }> {
  const api = window.electronAPI;
  if (!api?.getTableMetadata) {
    return { sql: null, error: 'Table metadata API not available' };
  }
  try {
    const result = await api.getTableMetadata({
      connectionId,
      database: ctx.database,
      schema: ctx.schema,
      tableName: ctx.tableName,
    });
    if (!result.success || !result.metadata) {
      return { sql: null, error: result.error || 'Failed to load table metadata' };
    }
    const sql = synthesizeCreateFromMetadata(ctx, result.metadata);
    return { sql, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to load table metadata';
    return { sql: null, error: message };
  }
}
